// src/components/Araclar.js

import React, { useState, useEffect, useMemo } from 'react';
import VehicleFormModal from './VehicleFormModal';
import { normalizeTr, aramaMetniUret } from '../utils/search';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, doc, deleteDoc, where, getDocs, writeBatch } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, TextField, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

const Araclar = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const q = query(collection(db, "vehicles"), orderBy("plaka", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => { console.error("Araçlar çekilirken hata oluştu: ", error); setLoading(false); });
    return () => unsubscribe();
  }, []);

  const handleAddVehicle = async (vehicleData) => {
    // Hata YUTULMAZ: modal kaydın başarısız olduğunu görüp açık kalmalı.
    // Tarih garantisi (Faz 5): yeni yazımlar eklenmeTarihi + createdAt İKİSİNİ de alır.
    await addDoc(collection(db, "vehicles"), { ...vehicleData, eklenmeTarihi: serverTimestamp(), createdAt: serverTimestamp() });
    alert("Araç başarıyla sisteme eklendi!");
  };

  const handleUpdateVehicle = async (id, updatedData) => {
    const vehicleRef = doc(db, 'vehicles', id);
    const workOrdersQuery = query(collection(db, 'workOrders'), where('aracId', '==', id));

    const batch = writeBatch(db);
    batch.update(vehicleRef, updatedData);

    // Yalnızca TANIMLI alanlar iş emrine yazılır. Eskiden `musteriAdSoyad` ve
    // `aracImageUrl` payload'da hiç bulunmadığı için her zaman `undefined` gidiyor,
    // `batch.update` senkron `FirebaseError: Unsupported field value: undefined`
    // fırlatıyordu; hata da yutulduğu için commit hiç çalışmıyor, kayıt sessizce
    // düşüyordu. Araç sahibi bu ekrandan değişmediği için `musteriAdSoyad`
    // iş emrinde zaten güncel — dokunulmuyor.
    const isEmriGuncellemesi = {};
    if (updatedData.plaka !== undefined) isEmriGuncellemesi.aracPlaka = updatedData.plaka;
    if (updatedData.marka !== undefined || updatedData.model !== undefined) {
      isEmriGuncellemesi.aracMarkaModel = `${updatedData.marka || ''} ${updatedData.model || ''}`.trim();
      // G9: ayrı alanlar da senkron tutulur (rapor marka/model sütunları).
      if (updatedData.marka !== undefined) isEmriGuncellemesi.aracMarka = updatedData.marka;
      if (updatedData.model !== undefined) isEmriGuncellemesi.aracModel = updatedData.model;
    }

    if (Object.keys(isEmriGuncellemesi).length > 0) {
      const workOrdersSnapshot = await getDocs(workOrdersQuery);
      workOrdersSnapshot.forEach(workOrderDoc => {
        batch.update(workOrderDoc.ref, isEmriGuncellemesi);
      });
    }

    await batch.commit();
  };

  const handleDeleteVehicle = async (id, plaka) => {
    const workOrdersRef = collection(db, 'workOrders');
    const q = query(workOrdersRef, where("aracId", "==", id));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        alert(`'${plaka}' plakalı araç, kendisine ait ${querySnapshot.size} adet iş emri bulunduğu için silinemez. Bir aracı silebilmek için önce o araca ait tüm iş emirlerini silmeniz gerekmektedir.`);
        return;
    }

    if (window.confirm(`'${plaka}' plakalı aracı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`)) {
      try {
        await deleteDoc(doc(db, 'vehicles', id));
        alert("Araç başarıyla silindi.");
      } catch (e) { 
        console.error("Araç silinirken hata: ", e); 
        alert("Araç silinirken bir hata oluştu.");
      }
    }
  };

  const handleOpenEditModal = (vehicle) => { setEditingVehicle(vehicle); setIsModalOpen(true); };
  const handleOpenAddModal = () => { setEditingVehicle(null); setIsModalOpen(true); };
  const handleCloseModal = () => { setIsModalOpen(false); setEditingVehicle(null); };

  // G8: Türkçe-normalize arama (plaka + sahip adı); plaka için boşluklar da katlanır
  // ("33ABC123" ↔ "33 ABC 123" eşleşir). Null-guard'lı.
  const filteredVehicles = useMemo(() => {
    const q = normalizeTr(searchTerm).replace(/\s/g, '');
    if (!q) return vehicles;
    return vehicles.filter(vehicle =>
      aramaMetniUret(vehicle.plaka, vehicle.musteriAdSoyad).replace(/\s/g, '').includes(q)
    );
  }, [vehicles, searchTerm]);
  
  if (!currentUser) {
    return <Box sx={{display: 'flex', justifyContent: 'center', mt: 4}}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1">Araç Yönetimi</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField size="small" variant="outlined" placeholder="Plaka veya sahip ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: ( <InputAdornment position="start"><SearchIcon /></InputAdornment> ) }} />
            {currentUser && currentUser.role !== 'Usta' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAddModal}>Yeni Araç Ekle</Button>
            )}
        </Box>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          {loading ? (<CircularProgress sx={{ display: 'block', margin: 'auto', my: 4 }} />) : (
            <Table stickyHeader>
              <TableHead><TableRow><TableCell>Plaka</TableCell><TableCell>Marka</TableCell><TableCell>Model</TableCell><TableCell>Araç Sahibi</TableCell><TableCell>İşlemler</TableCell></TableRow></TableHead>
              <TableBody>
                {filteredVehicles.map((vehicle) => (
                  <TableRow hover key={vehicle.id} >
                    <TableCell sx={{cursor: 'pointer'}} onClick={() => navigate(`/arac/${vehicle.id}`)}>{vehicle.plaka}</TableCell>
                    <TableCell>{vehicle.marka}</TableCell>
                    <TableCell>{vehicle.model || '-'}</TableCell>
                    <TableCell>{vehicle.musteriAdSoyad}</TableCell>
                    <TableCell>
                      {currentUser && currentUser.role !== 'Usta' && (
                        <Button size="small" variant="outlined" startIcon={<EditIcon />} sx={{ mr: 1 }} onClick={(e) => { e.stopPropagation(); handleOpenEditModal(vehicle); }}>Düzenle</Button>
                      )}
                      {currentUser && currentUser.role === 'Genel Müdür' && (
                        <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={(e) => { e.stopPropagation(); handleDeleteVehicle(vehicle.id, vehicle.plaka);}}>Sil</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
      <VehicleFormModal isOpen={isModalOpen} onClose={handleCloseModal} onAddVehicle={handleAddVehicle} onUpdateVehicle={handleUpdateVehicle} vehicleToEdit={editingVehicle} />
    </Box>
  );
};

export default Araclar;